import type { UseFormGetValues } from 'react-hook-form';

import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { NewEmailFormModel } from '@/components/emailWindow/model/NewEmailForm.model.ts';
import { useMapNewEmailToUpdateDraft } from '@/components/emailWindow/util/mapNewEmailToUpdateDraft.util.ts';
import { useAnyAttachmentIsNotReady } from '@/components/emailWindow/util/useAnyAttachmentIsNotReady.util.ts';
import { useSendEmailFromDraft } from '@/components/emailWindow/util/useSendEmailFromDraft.util.ts';
import { useUpdateEmailDraft } from '@/components/emailWindow/util/useUpdateEmailDraft.util.ts';
import { CONFIG } from '@/config/config.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import {
  EditedEmailState,
  useNewEmailAutosavePaused,
  useNewEmailDraftControllerState,
} from '@/views/emails/context/NewEmails.ts';

export type AutosaveStopReason = 'rejected' | 'unavailable';

type Params = {
  getFormValues: UseFormGetValues<NewEmailFormModel>;
};

type Return = {
  autosaveStopped: AutosaveStopReason | null;
  isSendingEmail: boolean;
  saveCurrentDraft: (mode?: SaveDraftMode) => Promise<boolean>;
  sendDraft: (formValues: NewEmailFormModel) => Promise<boolean>;
};

type SaveDraftMode = 'background' | 'final';

const MAX_CONSECUTIVE_FAILURES = 3;

const classifyFailure = (error: unknown): AutosaveStopReason => {
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500 ? 'rejected' : 'unavailable';
};

export const useEmailDraftController = ({ getFormValues }: Params): Return => {
  const autosavePaused = useNewEmailAutosavePaused();
  const draftControllerState = useNewEmailDraftControllerState();
  const { updateEmailDraft } = useUpdateEmailDraft();
  const { sendEmailFromDraft } = useSendEmailFromDraft();
  const { userId } = useUserContext();
  const { mapNewEmailToUpdateDraft } = useMapNewEmailToUpdateDraft();
  const anyAttachmentIsNotReady = useAnyAttachmentIsNotReady();
  const isComposerOpen = draftControllerState !== null;
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const autosavePausedRef = useRef(autosavePaused);
  const isSendingEmailRef = useRef(isSendingEmail);
  const isUpdatingDraftRef = useRef(false);
  const updateDraftPromiseRef = useRef<Promise<void> | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const [autosaveStopped, setAutosaveStopped] = useState<AutosaveStopReason | null>(null);
  const autosaveStoppedRef = useRef<AutosaveStopReason | null>(null);

  useEffect(() => {
    autosaveStoppedRef.current = autosaveStopped;
  }, [autosaveStopped]);

  useEffect(() => {
    autosavePausedRef.current = autosavePaused;
  }, [autosavePaused]);

  useEffect(() => {
    isSendingEmailRef.current = isSendingEmail;
  }, [isSendingEmail]);

  const saveDraft = useCallback(
    async (formValues: NewEmailFormModel, mode: SaveDraftMode = 'background') => {
      if (
        !draftControllerState ||
        draftControllerState.state === EditedEmailState.IN_PROGRESS ||
        anyAttachmentIsNotReady ||
        (mode === 'background' && isSendingEmailRef.current)
      ) {
        return false;
      }

      if (isUpdatingDraftRef.current) {
        if (mode === 'background' || !updateDraftPromiseRef.current) {
          return false;
        }
        try {
          await updateDraftPromiseRef.current;
        } catch {}
      }

      isUpdatingDraftRef.current = true;
      const updateDraftPromise = updateEmailDraft({
        data: mapNewEmailToUpdateDraft({
          form: formValues,
          onEmailId: draftControllerState.onEmailId,
          sendMode: draftControllerState.sendMode,
        }),
        emailId: draftControllerState.emailId,
        threadId: draftControllerState.threadId,
        userId,
      });
      updateDraftPromiseRef.current = updateDraftPromise;
      try {
        await updateDraftPromise;
        return true;
      } finally {
        isUpdatingDraftRef.current = false;
        if (updateDraftPromiseRef.current === updateDraftPromise) {
          updateDraftPromiseRef.current = null;
        }
      }
    },
    [
      anyAttachmentIsNotReady,
      draftControllerState,
      mapNewEmailToUpdateDraft,
      updateEmailDraft,
      userId,
    ],
  );

  const saveCurrentDraft = useCallback(
    async (mode: SaveDraftMode = 'background') => {
      return saveDraft(getFormValues(), mode);
    },
    [getFormValues, saveDraft],
  );

  const saveCurrentDraftRef = useRef(saveCurrentDraft);

  useEffect(() => {
    saveCurrentDraftRef.current = saveCurrentDraft;
  }, [saveCurrentDraft]);

  const runBackgroundSave = useCallback(() => {
    void saveCurrentDraftRef
      .current('background')
      .then(() => {
        consecutiveFailuresRef.current = 0;
        setAutosaveStopped(null);
      })
      .catch((error: unknown) => {
        const reason = classifyFailure(error);
        consecutiveFailuresRef.current += 1;
        if (reason === 'rejected' || consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
          setAutosaveStopped(reason);
        }
      });
  }, []);

  const editedDraftId =
    draftControllerState?.state === EditedEmailState.READY ? draftControllerState.emailId : null;

  useEffect(() => {
    consecutiveFailuresRef.current = 0;
    setAutosaveStopped(null);
  }, [editedDraftId]);

  useEffect(() => {
    if (!isComposerOpen) {
      return;
    }
    const intervalId = setInterval(() => {
      if (autosavePausedRef.current || autosaveStoppedRef.current !== null) {
        return;
      }
      runBackgroundSave();
    }, Number(CONFIG.EMAIL_DRAFT_AUTOSAVE));

    return () => {
      clearInterval(intervalId);
    };
  }, [isComposerOpen, runBackgroundSave]);

  useEffect(() => {
    if (autosavePaused) {
      runBackgroundSave();
    }
  }, [autosavePaused, runBackgroundSave]);

  useEffect(() => {
    return () => {
      if (isSendingEmailRef.current) {
        return;
      }
      runBackgroundSave();
    };
  }, [runBackgroundSave]);

  const sendDraft = useCallback(
    async (formValues: NewEmailFormModel) => {
      if (
        isSendingEmailRef.current ||
        !draftControllerState ||
        draftControllerState.state === EditedEmailState.IN_PROGRESS ||
        anyAttachmentIsNotReady
      ) {
        return false;
      }

      isSendingEmailRef.current = true;
      setIsSendingEmail(true);
      let sent = false;
      try {
        const wasDraftSaved = await saveDraft(formValues, 'final');
        if (!wasDraftSaved) {
          return false;
        }
        await sendEmailFromDraft({
          draftId: draftControllerState.emailId,
          threadId: draftControllerState.threadId,
          userId,
        });
        sent = true;
        return true;
      } catch {
        return false;
      } finally {
        if (!sent) {
          isSendingEmailRef.current = false;
          setIsSendingEmail(false);
        }
      }
    },
    [anyAttachmentIsNotReady, draftControllerState, saveDraft, sendEmailFromDraft, userId],
  );

  return {
    autosaveStopped,
    isSendingEmail,
    saveCurrentDraft,
    sendDraft,
  };
};
